import React from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, SxProps, Theme, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';

export type InfoTooltipVariant = 'standard' | 'popover';

type Props = {
  title: React.ReactNode;
  placement?: TooltipProps['placement'];
  fontSize?: number;
  /** Shown to screen readers when the icon is focusable. */
  ariaLabel?: string;
  /** `popover` matches API keys, webhooks, and insights (white tooltip panel). */
  variant?: InfoTooltipVariant;
  popoverWidth?: number;
  popoverMaxWidth?: number;
  popoverTextAlign?: 'left' | 'center';
  iconTone?: 'link' | 'muted';
  iconSx?: SxProps<Theme>;
  arrow?: boolean;
};

/** Shared white “popover” tooltip panel (API keys, webhooks, insights). */
export const popoverTooltipSx = ({
  width,
  maxWidth,
  textAlign = 'center',
  fontSize = '11px',
  lineHeight = '16px',
  px = '12px',
  py = '8px',
}: {
  width?: number;
  maxWidth?: number;
  textAlign?: 'left' | 'center';
  fontSize?: string;
  lineHeight?: string;
  px?: string;
  py?: string;
}) => ({
  ...(width !== undefined ? { width: `${width}px` } : {}),
  ...(maxWidth !== undefined ? { maxWidth: `${maxWidth}px` } : {}),
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'black',
    fontSize,
    fontWeight: 400,
    textAlign,
    boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    borderRadius: '8px',
    lineHeight,
    px,
    py,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'white',
  },
});

/**
 * Info icon with tooltip — shared across Fees, Paylinks, portal users, API keys, and webhooks.
 */
export const InfoTooltip = ({
  title,
  placement = 'right',
  fontSize = 14,
  ariaLabel = 'More information',
  variant = 'standard',
  popoverWidth,
  popoverMaxWidth,
  popoverTextAlign = 'center',
  iconTone = 'link',
  iconSx,
  arrow = true,
}: Props) => {
  const iconColor = iconTone === 'muted' ? (theme: Theme) => theme.palette.grey[500] : 'link.main';

  const componentsProps =
    variant === 'popover'
      ? {
          popper: {
            sx: popoverTooltipSx({
              width: popoverWidth,
              maxWidth: popoverMaxWidth,
              textAlign: popoverTextAlign,
            }),
          },
        }
      : undefined;

  return (
    <Tooltip
      enterTouchDelay={0}
      title={title}
      placement={placement}
      arrow={arrow}
      componentsProps={componentsProps}
    >
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <InfoOutlined
          sx={{
            fontSize,
            color: iconColor,
            cursor: 'default',
            ...iconSx,
          }}
          tabIndex={0}
          aria-label={ariaLabel}
        />
      </Box>
    </Tooltip>
  );
};
